import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
    AlertBannerType,
    ButtonFlavor,
    ButtonType,
    ExAlertBanner,
    ExButton,
    ExLink,
    ExPillGroup,
    LinkSize,
    PillColor,
    PillSize,
} from '@boomi/exosphere';
import {
    type IdpType,
    type CreateIdentityProvider,
    createIdentityProvider,
} from './identityProviders';
import IdentityProviderFormFields, {
    type FormValues,
    EMPTY_FORM_VALUES,
} from './IdentityProviderFormFields';
import styles from '../shared/Form.module.css';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';

const IdentityProviderCreate = () => {
    const navigate = useNavigate();

    const [type, setType] = useState<IdpType>('OAUTH');
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setError(null);
        setSubmitting(true);

        const payload: CreateIdentityProvider =
            type === 'OAUTH'
                ? {
                      type,
                      name: values.name,
                      allowedAudience: values.allowedAudience,
                      clientId: values.clientId,
                      clientSecret: values.clientSecret,
                      wellKnownUrl: values.wellKnownUrl,
                  }
                : {
                      type,
                      name: values.name,
                      allowedAudience: values.allowedAudience,
                      entityId: values.entityId,
                      metadata: values.metadata,
                  };

        try {
            await createIdentityProvider(payload);
            navigate('/identity-providers');
        } catch (err) {
            setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <ExLink onClick={() => navigate('/identity-providers')} leadingIcon={'Left arrow'}>
                    Identity Providers
                </ExLink>
                <h2 className={`${styles.heading} ex-text-h2`}>Add Identity Provider</h2>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
                <div className="ex-mb-medium">
                    <ExPillGroup
                        values={[
                            {
                                label: 'OAUTH',
                                interactive: true,
                                selected: type === 'OAUTH',
                                removable: false,
                                color: PillColor.BLUE,
                                size: PillSize.REGULAR,
                                icon: '',
                            },
                            {
                                label: 'SAML',
                                interactive: true,
                                selected: type === 'SAML',
                                removable: false,
                                color: PillColor.BLUE,
                                size: PillSize.REGULAR,
                                icon: '',
                            },
                        ]}
                        onClick={(e) =>
                            setType(
                                (e as CustomEvent<{ pillData: { label: string } }>).detail.pillData
                                    .label as IdpType,
                            )
                        }
                    />
                </div>

                <IdentityProviderFormFields type={type} values={values} onChange={handleChange} />

                {error && (
                    <ExAlertBanner
                        className={styles.saveError}
                        type={AlertBannerType.ERROR}
                        open
                        onClose={() => setError(null)}
                    >
                        {error}
                    </ExAlertBanner>
                )}

                <div className={styles.actions}>
                    <ExLink size={LinkSize.XSMALL} onClick={() => navigate('/identity-providers')}>
                        Cancel
                    </ExLink>
                    <ExButton
                        type={ButtonType.PRIMARY}
                        flavor={ButtonFlavor.BRANDED}
                        disabled={submitting}
                        onClick={() => handleSubmit()}
                    >
                        {submitting ? 'Adding…' : 'Add provider'}
                    </ExButton>
                </div>
            </form>
        </div>
    );
};

export default IdentityProviderCreate;
